import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { PageTitle } from "./AttDashboard";

const LABELS = { users: "Users / Logins", att_centers: "Centers", att_center_counters: "Center counters", att_scanner_settings: "Scanner settings", att_batches: "Batches", att_candidates: "Candidates", fingerprints: "Fingerprints", fp_audit_logs: "Audit logs" };

export default function AttMigration() {
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState({ source_url: "https://masharaskills.com", email: "", password: "" });
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadSummary = () => api.get("/attendance/migration/summary").then((r) => setSummary(r.data)).catch(() => {});
  useEffect(() => { loadSummary(); api.get("/attendance/migration/import/status").then((r) => setStatus(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (status?.status !== "running") return;
    const t = setInterval(() => api.get("/attendance/migration/import/status").then((r) => { setStatus(r.data); if (r.data.status !== "running") loadSummary(); }), 1500);
    return () => clearInterval(t);
  }, [status?.status]);

  const start = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/attendance/migration/import/start", form);
      toast.success("Import started");
      setStatus({ status: "running", collections: {} });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="att-migration-page">
      <PageTitle title="Data Migration" crumb="Migration" />
      <div className="grid lg:grid-cols-2 gap-6">
        <section className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-semibold text-slate-800 mb-3">This deployment's data</h3>
          <table className="w-full text-sm"><tbody>
            {summary && Object.entries(summary.collections).map(([k, v]) => (
              <tr key={k} className="border-t border-slate-100"><td className="py-1.5 text-slate-600">{LABELS[k] || k}</td><td className="py-1.5 text-right font-mono" data-testid={`mig-count-${k}`}>{v}</td></tr>
            ))}
          </tbody></table>
          <p className="text-xs text-slate-500 mt-3">Export API: <code>/api/attendance/migration/export</code> (admin only, read-only).</p>
        </section>
        <section className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-semibold text-slate-800 mb-1">Pull data from old deployment</h3>
          {summary && !summary.import_enabled ? (
            <p className="text-sm text-amber-700 bg-amber-50 rounded-lg p-3" data-testid="mig-import-disabled">Import is disabled here. It is only enabled on the new standalone deployment (MIGRATION_IMPORT_ENABLED=true).</p>
          ) : (
            <form onSubmit={start} className="space-y-3">
              <Input data-testid="mig-source-url" placeholder="Old site URL (https://masharaskills.com)" value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} required />
              <Input data-testid="mig-email" type="email" placeholder="Old site admin email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
              <Input data-testid="mig-password" type="password" placeholder="Old site admin password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
              <Button data-testid="mig-start-btn" type="submit" disabled={busy || status?.status === "running"} className="bg-emerald-700 hover:bg-emerald-800">{status?.status === "running" ? "Importing…" : "Start import (upsert, safe to re-run)"}</Button>
            </form>
          )}
          {status && status.status !== "idle" && (
            <div className="mt-4 text-sm" data-testid="mig-status">
              <p className="font-medium">Status: <span className={status.status === "failed" ? "text-red-600" : status.status === "done" ? "text-emerald-700" : "text-slate-700"}>{status.status}</span></p>
              {status.error && <p className="text-red-600 text-xs mt-1" data-testid="mig-error">{status.error}</p>}
              <table className="w-full mt-2"><tbody>
                {Object.entries(status.collections || {}).map(([k, v]) => (
                  <tr key={k} className="border-t border-slate-100"><td className="py-1 text-slate-600">{LABELS[k] || k}</td><td className="py-1 text-right font-mono">{v.imported}/{v.source}</td><td className="py-1 text-right text-xs">{v.status === "ok" ? "✓" : v.status === "mismatch" ? "⚠ mismatch" : "…"}</td></tr>
                ))}
              </tbody></table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
